import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapUpdaterProps, MapViewProps } from '../types';
import { PropertyMarker } from './map-search';

// Fix for default marker icons in React Leaflet
interface IconDefaultPrototype {
  _getIconUrl?: () => string;
}

delete (L.Icon.Default.prototype as IconDefaultPrototype)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom component to update map center
const MapUpdater: React.FC<MapUpdaterProps> = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

const MapView: React.FC<MapViewProps> = ({
  properties,
  center = [-25.2867, -57.5759],
  zoom = 8,
  selectedPropertyId,
  hoveredPropertyId,
  showPriceMarkers = false,
  onMarkerClick,
  onMarkerHover,
}) => {
  const { getTextInCurrentLanguage } = useLanguage();

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Default handlers for backward compatibility
  const handleMarkerClick = (propertyId: string) => {
    if (onMarkerClick) {
      onMarkerClick(propertyId);
    }
  };

  const handleMarkerHover = (propertyId: string | null) => {
    if (onMarkerHover) {
      onMarkerHover(propertyId);
    }
  };

  return (
    <div className="h-full w-full rounded-lg overflow-hidden shadow-lg">
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom={true}
        className="h-full w-full"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapUpdater center={center} />

        {properties.map((property) =>
          showPriceMarkers ? (
            <PropertyMarker
              key={property.id}
              property={property}
              isSelected={selectedPropertyId === property.id}
              isHovered={hoveredPropertyId === property.id}
              onClick={handleMarkerClick}
              onHover={handleMarkerHover}
            />
          ) : (
            <Marker
              key={property.id}
              position={[property.latitude, property.longitude]}
            >
              <Popup>
                <Link to={`/property/${property.id}`} className="block">
                  <div className="w-48">
                    <img
                      src={property.mainImage}
                      alt={getTextInCurrentLanguage(property.title)}
                      className="w-full h-32 object-cover rounded-lg mb-2"
                    />
                    <div className="font-bold text-lg text-blue-600 mb-1">
                      {formatPrice(property.price)}
                    </div>
                    <div className="text-sm text-gray-700 font-medium mb-1">
                      {property.beds} bd | {property.baths} ba | {property.sqft.toLocaleString()} sqft
                    </div>
                    <div className="text-xs text-gray-600">
                      {property.address}, {property.city}
                    </div>
                  </div>
                </Link>
              </Popup>
            </Marker>
          )
        )}
      </MapContainer>
    </div>
  );
};

export default MapView;
